
import { useState } from "react";
import { Link } from "react-router-dom";
import { Beaker, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useUniversityLabData } from "@/components/profile/university/hooks/useUniversityLabData";
import UniversitySelector from "@/components/profile/university/UniversitySelector";
import LabSelector from "@/components/profile/university/LabSelector";
import UniversityForm from "@/components/profile/university/UniversityForm";
import LabForm from "@/components/profile/university/LabForm";
import { University, Lab } from "@/services/universityService";

const RegisterPage = () => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showUniversityForm, setShowUniversityForm] = useState(false);
  const [showLabForm, setShowLabForm] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { signUp } = useAuth();

  const {
    universities,
    labs,
    selectedUniversity,
    setSelectedUniversity,
    updateUniversities,
    updateLabs
  } = useUniversityLabData(null);

  const [selectedLab, setSelectedLab] = useState<string>("");

  const validateForm = () => {
    setErrorMessage("");
    
    if (!email.trim()) {
      setErrorMessage("Email is required");
      return false;
    }
    
    if (!username.trim()) {
      setErrorMessage("Username is required");
      return false;
    }

    if (!selectedUniversity) {
      setErrorMessage("Please select a university");
      return false;
    }

    if (!selectedLab) {
      setErrorMessage("Please select a lab");
      return false;
    }
    
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters");
      return false;
    }
    
    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      setErrorMessage("Password must contain at least one uppercase letter");
      return false;
    }
    
    // Check for special character
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      setErrorMessage("Password must contain at least one special character");
      return false;
    }
    
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return false;
    }
    
    if (!acceptTerms) {
      setErrorMessage("You must agree to the Terms & Conditions and Privacy Policy");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrorMessage("");
    
    try {
      // Include university and lab data in the signup metadata
      await signUp(email.trim(), password, {
        display_username: username.trim(),
        university_id: selectedUniversity,
        lab_id: selectedLab,
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Registration error:", error);
      if (error instanceof Error) {
        setErrorMessage(error.message || "Registration failed");
      } else {
        setErrorMessage("Registration failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUniversityChange = (value: string) => {
    if (value === "add_new") {
      setShowUniversityForm(true);
      return;
    }
    setSelectedUniversity(value);
    setSelectedLab(""); // Reset lab when university changes
  };

  const handleLabChange = (value: string) => {
    if (value === "add_new") {
      setShowLabForm(true);
      return;
    }
    setSelectedLab(value);
  };

  const handleUniversityFormSubmit = (university: University) => {
    updateUniversities(university);
    setSelectedUniversity(university.id);
    setShowUniversityForm(false);
  };

  const handleLabFormSubmit = (lab: Lab) => {
    updateLabs(lab);
    setSelectedLab(lab.id);
    setShowLabForm(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8 text-center">
            <div>
              <Beaker className="h-12 w-12 text-reagent-blue mx-auto" />
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Registration Submitted
              </h2>
              <p className="mt-4 text-sm text-gray-600">
                Thank you for registering for LabSwApp! Your account is pending approval by an administrator.
                You will receive an email notification once your account has been approved and you can start using the platform.
              </p>
              <div className="mt-6">
                <Link to="/login" className="text-reagent-blue hover:underline">
                  Return to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Beaker className="h-12 w-12 text-reagent-blue mx-auto" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Create an account
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Or{" "}
              <Link to="/login" className="text-reagent-blue hover:underline">
                sign in to your account
              </Link>
            </p>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {errorMessage && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {errorMessage}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="username">Username to be displayed</Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 mt-1"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Passwords must be at least 8 characters in length using at least one upper case and one special character.
                </p>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1"
                  />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 mt-1"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="university">University</Label>
                {showUniversityForm ? (
                  <UniversityForm
                    isOpen={showUniversityForm}
                    onClose={() => setShowUniversityForm(false)}
                    onSuccess={handleUniversityFormSubmit}
                  />
                ) : (
                  <div className="mt-1">
                    <UniversitySelector
                      universities={universities}
                      value={selectedUniversity || ""}
                      onChange={handleUniversityChange}
                      onAddNew={() => setShowUniversityForm(true)}
                    />
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="lab">Lab</Label>
                {showLabForm ? (
                  <LabForm
                    isOpen={showLabForm}
                    onClose={() => setShowLabForm(false)}
                    onSuccess={handleLabFormSubmit}
                    universityId={selectedUniversity}
                  />
                ) : (
                  <div className="mt-1">
                    <LabSelector
                      labs={labs}
                      value={selectedLab}
                      onChange={handleLabChange}
                      onAddNew={() => setShowLabForm(true)}
                      universitySelected={!!selectedUniversity}
                      disabled={!selectedUniversity}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Terms & Conditions and Privacy Notice</h4>
                  <p className="text-sm text-gray-600">
                    By creating an account on The LabSwAPP marketplace, you agree to our{" "}
                    <a href="https://www.labswapp.com/terms" target="_blank" rel="noopener noreferrer" className="text-reagent-blue hover:underline">
                      Terms & Conditions
                    </a>{" "}
                    and{" "}
                    <a href="https://www.labswapp.com/privacy" target="_blank" rel="noopener noreferrer" className="text-reagent-blue hover:underline">
                      Privacy Policy
                    </a>
                    . This includes:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                    <li>Your account details, user data, and transaction history being securely stored and processed for the purpose of operating the marketplace.</li>
                    <li>The ability to manage your own notification preferences at any time.</li>
                    <li>The option to delete your account and associated data at any time, subject to legal and regulatory requirements.</li>
                  </ul>
                  <p className="text-sm text-gray-600">
                    We process your data in compliance with applicable data protection laws, including the GDPR (Europe) and relevant US privacy regulations.
                  </p>
                  
                  <div className="flex items-start space-x-3 pt-2">
                    <Checkbox
                      id="terms"
                      checked={acceptTerms}
                      onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="terms" className="text-sm text-gray-900 cursor-pointer leading-5">
                      I have read and agree to the Terms & Conditions and Privacy Policy
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600">
              Your registration will be reviewed by an administrator before you can access the platform.
            </p>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? "Creating account..." : "Create account"}
            </Button>
          </form>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default RegisterPage;
